import styled from '@emotion/styled'

import { ApiPortfolioType } from '@/api'
import {
  Alert,
  AssetTable,
  Break,
  DurationButtons,
  Fees,
  Icon,
  IconButton,
  PortfolioDetails,
  Section,
  Space,
  StrategyDisplay,
  Typography
} from '@/components/core-v2'
import { PORTFOLIO_TYPE } from '@/constants'

import { useAppSelector } from '../../../store/hooks'
import { getMergedDisallowedStrategies } from '../../00-builder/state/mappers'
import { StyledCharges } from '../../01-note/existing-note/details-summary/primitives'
import { ChoosePortfolioModal } from '../choose-portfolio'
import {
  ModelPortfolioState,
  PortfolioStepActions,
  SearchPortfolioState
} from '../state/portfolio-slice'
import { PortfolioTitle } from './portfolio-title'

interface PortfolioEditorProps {
  actions: PortfolioStepActions
  portfolioType: ApiPortfolioType
  searchState: SearchPortfolioState
  state: ModelPortfolioState
}

const StyledSection = styled(Section)``

const NotChosen = ({
  onClick,
  portfolioType
}: {
  onClick: () => void
  portfolioType: ApiPortfolioType
}) => {
  const title = portfolioType
    ? `Search for a ${PORTFOLIO_TYPE[portfolioType].value} portfolio`
    : 'Search for a portfolio'
  return (
    <Section pb={6} type="primary">
      <Space mt={6} mx={5}>
        <StyledSection type="secondary">
          <Space isflex justifyContent="center" p={6}>
            <IconButton
              data-testid="search-for-portfolio"
              disabled={false}
              icon={<Icon name="search" size="default" />}
              iconPosition="left"
              operationType="progress"
              text={title}
              onClick={onClick}
            />
          </Space>
        </StyledSection>
      </Space>
    </Section>
  )
}

export const ActiveEditor = ({ actions, state }: Omit<PortfolioEditorProps, 'searchState'>) => {
  if (!state.portfolio) {
    return (
      <Space mx={5} py={6}>
        Loading...
      </Space>
    )
  }

  const { assets } = state
  const strategies = state.availableStrategies

  return (
    <Section pb={6} type="primary">
      <PortfolioTitle
        title={state.portfolio.portfolioName}
        onClickChooseAnotherPorfolio={() => actions.chooseModelPortfolio()}
      />

      <Space mb={6} mx={5}>
        <Space mb={5}>
          <DurationButtons
            items={['6m', '1y', '3y', '5y', '10y']}
            selectedItem={state.selectedAssetDuration}
            onSelect={item => actions.setSelectedAssetDuration(item)}
          />
        </Space>
        <Space pb={5}>
          <Break />
        </Space>
        <AssetTable
          assets={assets}
          investmentWeights={state.portfolio.investmentWeights}
          isFundOfFunds={PORTFOLIO_TYPE[state.portfolio.type].key === 'FundOfFunds'}
          onClickAsset={assetId => actions.inspectAsset(assetId)}
        />
      </Space>

      {state.isTaxClassificationWarning && !state.isInvalidTaxClassification && (
        <Space m={5}>
          <Alert title="This portfolio's tax classification does not match the note" type="error">
            You may wish to change the portfolio to maintain a consistent tax clasification
          </Alert>
        </Space>
      )}

      {state.isInvalidTaxClassification && (
        <Space m={5}>
          <Alert title="This portfolio's tax classification is invalid" type="error">
            You must change the portfolio in order to proceed
          </Alert>
        </Space>
      )}

      <Section mx={5} px={5} py={6} type="secondary">
        <PortfolioDetails
          calculatedMultiplier={state.calculatedMultiplier}
          portfolio={state.portfolio}
        />
        <Space isflex alignItems="center" flexDirection="column" pb={6}>
          <Space my={6}>
            <Typography fontSize="13px" fontWeight="800" variant="b3">
              Available strategies (choose in the next step)
            </Typography>
          </Space>
          <StrategyDisplay strategies={strategies} />
        </Space>
        <StyledCharges>{state.fees && <Fees fees={state.fees} title="Charges" />}</StyledCharges>
      </Section>
    </Section>
  )
}

export const ModelPortfolioEditor = ({
  actions,
  portfolioType,
  searchState,
  state
}: PortfolioEditorProps) => {
  const builderState = useAppSelector(state => state.builder)
  const businessUnitDisallowedStrategies = builderState.businessUnit?.disallowedStrategies
  return (
    <>
      {!state.isChosen && (
        <NotChosen portfolioType={portfolioType} onClick={() => actions.chooseModelPortfolio()} />
      )}
      {state.isChoosing && (
        <ChoosePortfolioModal
          portfolioType={portfolioType}
          state={searchState}
          onChangeSearchTerm={term => actions.changeSearchTerm(term)}
          onClickBackToSearch={() => actions.goBackToSearch()}
          onInspectPortfolio={id => actions.inspectPortfolio(id)}
          onSelectPortfolio={portfolio =>
            actions.setModelPortfolio({
              model: portfolio as any,
              disallowedStrategiesFromState: getMergedDisallowedStrategies(
                businessUnitDisallowedStrategies,
                portfolio.disallowedStrategies
              )
            })
          }
          onStopChoosingPortfolio={() => actions.stopChoosingPortfolio()}
        />
      )}

      {state.isChosen && (
        <ActiveEditor actions={actions} portfolioType={portfolioType} state={state} />
      )}
    </>
  )
}
